/**
 * Workspace context — holds the currently selected workspace and its detail.
 *
 * Provides workspace_key, capabilities, and display_name to all child pages
 * so they can gate features and fetch scoped data.
 */

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import type { WorkspaceDetail } from '../api-client';
import * as api from '../api-client';

interface WorkspaceContextValue {
  workspace: WorkspaceDetail | null;
  isLoading: boolean;
  error: string | null;
  selectWorkspace: (key: string, publicWorkspaceId?: string) => Promise<WorkspaceDetail>;
  restoreWorkspaceSelection: () => Promise<WorkspaceDetail | null>;
  clearWorkspace: () => void;
  hasCapability: (cap: string) => boolean;
}

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null);
const STORAGE_KEY = 'matcora-admin.public-workspace-id';

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const [workspace, setWorkspace] = useState<WorkspaceDetail | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectWorkspace = useCallback(async (key: string, publicWorkspaceId?: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const detail = await api.getWorkspace(key);
      if (!detail.public_workspace_id) {
        throw new Error('Workspace configuration is incomplete');
      }
      setWorkspace(detail);
      window.sessionStorage.setItem(
        STORAGE_KEY,
        publicWorkspaceId ?? detail.public_workspace_id,
      );
      return detail;
    } catch (err) {
      const msg = err instanceof api.AdminApiError ? err.detail : 'Failed to load workspace';
      setError(msg);
      throw err instanceof Error ? err : new Error(msg);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const restoreWorkspaceSelection = useCallback(async (): Promise<WorkspaceDetail | null> => {
    const storedPublicWorkspaceId = window.sessionStorage.getItem(STORAGE_KEY);
    if (!storedPublicWorkspaceId) return null;
    try {
      const workspaces = await api.listWorkspaces();
      const match = workspaces.find(
        (workspace) => workspace.public_workspace_id === storedPublicWorkspaceId,
      );
      if (!match) {
        window.sessionStorage.removeItem(STORAGE_KEY);
        return null;
      }
      return await selectWorkspace(match.workspace_key, match.public_workspace_id);
    } catch {
      window.sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
  }, [selectWorkspace]);

  const clearWorkspace = useCallback(() => {
    setWorkspace(null);
    setError(null);
    window.sessionStorage.removeItem(STORAGE_KEY);
  }, []);

  const hasCapability = useCallback(
    (cap: string) => workspace?.capabilities.includes(cap) ?? false,
    [workspace],
  );

  const value = useMemo(
    () => ({
      workspace,
      isLoading,
      error,
      selectWorkspace,
      restoreWorkspaceSelection,
      clearWorkspace,
      hasCapability,
    }),
    [workspace, isLoading, error, selectWorkspace, restoreWorkspaceSelection, clearWorkspace, hasCapability],
  );

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

export function useWorkspace(): WorkspaceContextValue {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) {
    throw new Error('useWorkspace must be used within WorkspaceProvider');
  }
  return ctx;
}
